import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { CreditCard, ChevronLeft, ChevronRight } from 'lucide-react';

export default function AdminTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminAPI.getTransactions({ page, limit: 20 })
      .then(({ data }) => { setTransactions(data.transactions); setTotal(data.total); setPages(data.pages); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <div data-testid="admin-transactions">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Transactions</h1>
        <p className="text-sm text-zinc-500 mt-1">{total} total transactions</p>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-transactions-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Transaction ID</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Type</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Amount</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Tier</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => <tr key={i} className="border-b border-zinc-100"><td colSpan={6} className="px-4 py-3"><div className="h-4 bg-zinc-100 rounded animate-pulse" /></td></tr>)
              ) : transactions.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-zinc-400">
                  <CreditCard className="w-8 h-8 text-zinc-200 mx-auto mb-2" />
                  No transactions yet
                </td></tr>
              ) : transactions.map(t => (
                <tr key={t.transaction_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3 text-xs font-mono text-zinc-500">{t.transaction_id}</td>
                  <td className="px-4 py-3 capitalize text-zinc-700">{t.type}</td>
                  <td className="px-4 py-3 font-semibold text-zinc-900">${t.amount?.toFixed(2)} {t.currency?.toUpperCase()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${t.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : t.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-600'}`}>
                      {t.payment_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 capitalize">{t.metadata?.tier?.replace('_', ' ') || '-'}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500">{new Date(t.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 bg-zinc-50">
            <p className="text-xs text-zinc-500">Page {page} of {pages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
